import dayjs from 'dayjs';

export const isValidPhoneNumberInput = (
  value: string,
  min: number = 7,
  max: number = 15
): boolean => {
  const raw = (value ?? '').trim();
  if (raw.length === 0) return false;

  // Explicitly reject letters so they can't be "normalized away"
  if (/[A-Za-zÅÄÖåäö]/.test(raw)) return false;

  // Allow only typical phone formatting characters in the raw input
  if (!/^[0-9+\s().-]+$/.test(raw)) return false;

  // '+' at most once and only at the beginning
  const plusCount = (raw.match(/\+/g) ?? []).length;
  if (plusCount > 1) return false;
  if (plusCount === 1 && !raw.startsWith('+')) return false;

  const cleaned = normalizePhoneNumber(raw); // digits only
  return new RegExp(`^[0-9]{${min},${max}}$`).test(cleaned);
};

export const toComparablePhone = (value?: string): string | undefined => {
  const raw = (value ?? '').trim();
  if (raw.length === 0) return undefined;
  if (!isValidPhoneNumberInput(raw)) return undefined; // don't compare invalid values
  return normalizePhoneNumber(raw);
};

export const normalizePhoneNumber = (phoneNumber: string): string => {
  // Ta bort alla tecken som inte är siffror
  let digits = phoneNumber.replace(/\D/g, '');

  // Hantera internationellt prefix '00'
  if (digits.startsWith('0046')) {
    digits = '0' + digits.slice(4);
  } else if (digits.startsWith('46')) {
    digits = '0' + digits.slice(2);
  }

  // Om numret börjar med 7 (mobilnummer) utan 0
  else if (digits.length === 9 && digits.startsWith('7')) {
    digits = '0' + digits;
  }

  return digits;
};

export const isPersonalNumber = (personalNumber: string) =>
  personalNumber.length === 12 &&
  isValidLuhnAlgorithm(personalNumber) &&
  isValidPersonalNumberDate(personalNumber);

const isValidLuhnAlgorithm = (personalNumber: string) => {
  const digits = personalNumber.replace(/\D/g, '');
  if (digits.length < 10) return false;
  const checkDigits = digits.slice(-10);
  const personalNumberArray = checkDigits.split('').reverse();
  var sum = personalNumberArray
    .map((n) => Number(n))
    .reduce((previous, current, index) => {
      if (index % 2) current *= 2;
      if (current > 9) current -= 9;
      return previous + current;
    }, 0);
  return sum % 10 === 0;
};

const isValidPersonalNumberDate = (personalNumber: string) => {
  const dateStr = personalNumber.substring(0, 8);
  const date = dayjs(dateStr, 'YYYYMMDD', true);
  return date.isValid();
};
